import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Undo2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { fetchInsider5pctData, fetchInsider5pctLatestDate, fetchLaporanKepemilikanData } from '../../utils/insiderBlob';
import { loadBrokerMap, resolveBrokerCode, BrokerEntry } from '../../utils/brokerCache';
import { evaluateFilterExpression, type FilterCell } from '../../utils/columnFilter';

interface InsiderData {
  "No"?: number;
  "Kode Efek"?: string;
  "Nama Emiten"?: string;
  "Nama Pemegang Rekening Efek"?: string;
  "Nama Pemegang Saham"?: string;
  "Nama Rekening Efek"?: string;
  "Alamat"?: string;
  "Alamat (Lanjutan)"?: string;
  "Kebangsaan"?: string;
  "Domisili"?: string;
  "Status (Lokal/Asing)"?: string;
  "Jumlah Saham (Hari Sebelumnya)"?: string | number;
  "Saham Gabungan (Hari Sebelumnya)"?: string;
  "Persentase (Hari Sebelumnya)"?: string | number;
  "Jumlah Saham (Hari Ini)"?: string | number;
  "Saham Gabungan (Hari Ini)"?: string;
  "Persentase (Hari Ini)"?: string | number;
  "Perubahan"?: string | number;
  _date?: string;
  _source?: string;
}

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useEffect(() => {
    const timeout = setTimeout(() => { onChangeRef.current(value); }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return <input {...props} value={value} onChange={e => setValue(e.target.value)} />;
}

type SortKey = 'date' | 'kode' | 'pemegang' | 'rekening' | 'sebelum' | 'sesudah' | 'perubahan' | 'status' | 'exchange' | 'source';

// Broker type classification
const FOREIGN_BROKERS = [
  'AG','AH','AI','AK','BK','BQ','CG','CS','DP','DR','DU','FS','GW','HD','KK',
  'KZ','LH','LG','LS','MS','RB','RX','TX','YP','YU','ZP'
];
const GOVERNMENT_BROKERS = ['CC','NI','OD','DX'];

const getBrokerColor = (code: string | null): string => {
  if (!code) return 'text-gray-400';
  if (GOVERNMENT_BROKERS.includes(code)) return 'text-green-500 font-semibold';
  if (FOREIGN_BROKERS.includes(code)) return 'text-red-400 font-semibold';
  return 'text-white font-semibold';
};

const sharesToLot = (val: string | number | undefined, source?: string): number => {
  if (val === undefined || val === null || val === '') return NaN;
  const divisor = source === 'idx' ? 1000 : 100;
  if (typeof val === 'number') return isNaN(val) ? NaN : val / divisor;
  
  let str = String(val).replace(/%/g, '').trim();
  str = str.replace(/\./g, '');
  str = str.replace(/,/g, '.');
  const num = Number(str);
  if (isNaN(num)) return NaN;
  return num / divisor;
};

const formatLot = (val: string | number | undefined, source?: string): string => {
  const lot = sharesToLot(val, source);
  if (isNaN(lot)) return '-';
  return lot.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

interface InsiderProps {
  isDashboard?: boolean;
  selectedStock?: string;
}

export const Insider = ({ isDashboard = false, selectedStock }: InsiderProps) => {
  const [dateFrom, setDateFrom] = useState<string>(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);
  const [dataType, setDataType] = useState<'volume' | 'percent'>('volume');
  const [appliedDataType, setAppliedDataType] = useState<'volume' | 'percent'>('volume');
  const [dataSource, setDataSource] = useState<'idx' | 'emiten' | 'all'>('idx');
  const [data, setData] = useState<InsiderData[]>([]);
  const [loading, setLoading] = useState(false);
  const [brokers, setBrokers] = useState<BrokerEntry[]>([]);
  const { showToast } = useToast();

  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'kode',
    direction: 'asc',
  });

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({
    kode: selectedStock || ''
  });

  useEffect(() => {
    if (selectedStock) {
      setColumnFilters(prev => ({ ...prev, kode: selectedStock }));
    }
  }, [selectedStock]);

  useEffect(() => {
    loadBrokerMap().then(setBrokers).catch(err => {
      console.warn('Could not load broker map:', err);
    });
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const latestDailyDate = await fetchInsider5pctLatestDate();
        
        let reportDataResult: InsiderData[] = [];
        try {
          const res = await fetchLaporanKepemilikanData();
          reportDataResult = res.data || [];
        } catch (e) {
          console.warn('Failed to fetch report data', e);
        }

        let latestReportDate: string | null = null;
        if (reportDataResult.length > 0) {
          const dates = reportDataResult.map(r => r._date).filter((d): d is string => !!d).sort();
          if (dates.length > 0) {
            latestReportDate = dates[dates.length - 1];
          }
        }

        let latestDate = latestDailyDate;
        if (!latestDate) latestDate = latestReportDate;
        else if (latestReportDate && latestReportDate > latestDate) latestDate = latestReportDate;

        if (latestDate) {
          if (isDashboard) {
            const d = new Date(latestDate);
            d.setDate(d.getDate() - 30);
            const fromStr = d.toISOString().split('T')[0];
            setDateFrom(fromStr);
            setDateTo(latestDate);
            
            const promises = [];
            let curr = new Date(fromStr);
            const end = new Date(latestDate);
            while (curr <= end) {
              const dStr = curr.toISOString().split('T')[0];
              promises.push(
                fetchInsider5pctData(dStr)
                  .then(res => res.data ? res.data.map((r: InsiderData) => ({ ...r, _date: dStr, _source: 'idx' })) : [])
                  .catch(() => [])
              );
              curr.setDate(curr.getDate() + 1);
            }
            const results = await Promise.all(promises);
            let combinedData = results.flat();

            const filteredReportData = reportDataResult.filter(r => r._date && r._date >= fromStr && r._date <= latestDate!);
            
            if (dataSource === 'all' || dataSource === 'emiten') {
               if (dataSource === 'emiten') combinedData = filteredReportData;
               else combinedData = [...combinedData, ...filteredReportData];
            }

            setData(combinedData);
          } else {
            setDateFrom(latestDate);
            setDateTo(latestDate);
            
            let combinedData: InsiderData[] = [];
            if (dataSource === 'idx' || dataSource === 'all') {
              const response = await fetchInsider5pctData(latestDate);
              if (response.data) {
                combinedData = [...combinedData, ...response.data.map((r: InsiderData) => ({ ...r, _date: latestDate, _source: 'idx' }))];
              }
            }
            if (dataSource === 'emiten' || dataSource === 'all') {
              const filteredReportData = reportDataResult.filter(r => r._date === latestDate);
              combinedData = [...combinedData, ...filteredReportData];
            }
            setData(combinedData);
          }
        }
      } catch (error) {
        console.error('Failed to load initial insider data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [isDashboard]);

  const handleFetch = async () => {
    if (!dateFrom) return;
    setLoading(true);
    setAppliedDataType(dataType);
    try {
      let dailyData: InsiderData[] = [];
      if (dataSource === 'idx' || dataSource === 'all') {
        const promises = [];
        let currentDate = new Date(dateFrom);
        const end = new Date(dateTo);
        while (currentDate <= end) {
          const dateStr = currentDate.toISOString().split('T')[0];
          promises.push(
            fetchInsider5pctData(dateStr)
              .then(res => res.data ? res.data.map((r: InsiderData) => ({ ...r, _date: dateStr, _source: 'idx' })) : [])
              .catch(() => [])
          );
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        const results = await Promise.all(promises);
        dailyData = results.flat();
      }

      let reportData: InsiderData[] = [];
      if (dataSource === 'emiten' || dataSource === 'all') {
        const res = await fetchLaporanKepemilikanData();
        if (res.data) {
          reportData = res.data.filter((r: any) => {
            if (!r._date) return false;
            return r._date >= dateFrom && r._date <= dateTo;
          });
        }
      }

      const allData = [...dailyData, ...reportData];

      if (allData.length > 0) {
        setData(allData);
      } else {
        setData([]);
        showToast({
          type: 'warning',
          title: 'Tidak ada data',
          message: 'Tidak ada data kepemilikan untuk rentang tanggal tersebut',
        });
      }
    } catch (error: any) {
      console.error(error);
      setData([]);
      showToast({
        type: 'error',
        title: 'Gagal memuat',
        message: 'Gagal memuat data dari penyimpanan.',
      });
    } finally {
      setLoading(false);
    }
  };

  const parseNum = (val: string | number | undefined, source?: string, isPercent?: boolean): number => {
    if (val === undefined || val === null || val === '') return NaN;
    if (typeof val === 'number') return val;
    let str = String(val).replace(/%/g, '').trim();

    if (source === 'emiten') {
      if (str.includes(',') && str.includes('.')) {
        str = str.replace(/\./g, '').replace(/,/g, '.');
      } else if (str.includes(',')) {
        str = str.replace(/,/g, '.');
      }
      let num = Number(str);
      if (isPercent && num > 100) {
         const digits = str.replace(/\./g, '');
         if (digits.length > 2) {
            num = Number(`${digits.substring(0, 2)}.${digits.substring(2)}`);
         }
      }
      return num;
    } else {
      // For IDX source, the data is already in standard format (e.g., 86.76)
      // Just parse it directly without stripping dots
      return Number(str);
    }
  };

  const renderPercent = (val: string | number | undefined, source?: string) => {
    if (val === undefined || val === null || val === '-' || val === '') return '0%';
    const num = parseNum(val, source, true);
    if (isNaN(num)) return '0%';
    if (source === 'emiten') {
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%`;
    }
    return `${num.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  };

  const getChangeColor = (row: any): string => {
    const num = parseNum(row['Perubahan'], row._source);
    if (isNaN(num) || num === 0) return 'text-gray-400';
    return num > 0 ? 'text-[#00ff00]' : 'text-[#ff0000]';
  };

  const formatDisplayDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const deduplicatedData = useMemo(() => {
    const seen = new Set<string>();
    return data.filter(row => {
      const key = [
        row._date ?? '',
        row['Kode Efek'] ?? '',
        row['Nama Rekening Efek'] ?? '',
        row['Nama Pemegang Saham'] ?? '',
        String(row['Jumlah Saham (Hari Sebelumnya)'] ?? ''),
        String(row['Jumlah Saham (Hari Ini)'] ?? ''),
        String(row['Persentase (Hari Sebelumnya)'] ?? ''),
        String(row['Persentase (Hari Ini)'] ?? ''),
        String(row['Perubahan'] ?? ''),
        row['Status (Lokal/Asing)'] ?? '',
        row._source ?? '',
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data]);

  const exchangeMap = useMemo(() => {
    const groups: Record<string, InsiderData[]> = {};
    deduplicatedData.forEach(row => {
      const gk = `${row._date ?? ''}__${row['Kode Efek'] ?? ''}__${row['Nama Pemegang Saham'] ?? ''}`;
      if (!groups[gk]) groups[gk] = [];
      groups[gk].push(row);
    });

    const transferSet = new Set<InsiderData>();
    Object.values(groups).forEach(rows => {
      const totalChange = rows.reduce((sum, r) => {
        const v = parseNum(r['Perubahan'], r._source);
        return sum + (isNaN(v) ? 0 : v);
      }, 0);

      if (Math.abs(totalChange) < 0.001 && rows.length > 1) {
        rows.forEach(r => transferSet.add(r));
      }
    });

    return transferSet;
  }, [deduplicatedData]);

  const getExchange = (row: InsiderData): string => {
    if (exchangeMap.has(row)) return 'Transfer';
    const chg = parseNum(row['Perubahan'], row._source);
    if (isNaN(chg) || chg === 0) return '-';
    return chg > 0 ? 'Buy' : 'Sell';
  };

  const getExchangeColor = (ex: string): string => {
    if (ex === 'Buy') return 'text-[#00ff00]';
    if (ex === 'Sell') return 'text-[#ff0000]';
    if (ex === 'Transfer') return 'text-yellow-400';
    return 'text-gray-400';
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const SortIndicator = ({ col }: { col: SortKey }) => {
    if (sortConfig.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  // Sel filter per field: nilai numerik persis seperti filter lama,
  // warna sel persis seperti yang dirender
  const getCellForField = (r: InsiderData, field: string): FilterCell => {
    const isPercent = appliedDataType === 'percent';
    const rekeningRaw = r['Nama Rekening Efek'] ?? '';
    switch (field) {
      case 'date':
        return { value: NaN, text: String(r._date ?? '').toLowerCase(), colorClass: 'text-gray-400', isNumeric: false };
      case 'kode':
        return { value: NaN, text: String(r['Kode Efek'] ?? '').toLowerCase(), colorClass: 'text-white', isNumeric: false };
      case 'pemegang':
        return { value: NaN, text: String(r['Nama Pemegang Saham'] ?? '').toLowerCase(), colorClass: 'text-gray-300', isNumeric: false };
      case 'rekening':
        return { value: NaN, text: rekeningRaw.toLowerCase(), colorClass: getBrokerColor(resolveBrokerCode(rekeningRaw, brokers)), isNumeric: false };
      case 'status': {
        const raw = String(r['Status (Lokal/Asing)'] ?? '');
        const display = raw === 'L' ? 'd' : raw === 'A' ? 'f' : raw.toLowerCase();
        return { value: NaN, text: `${display} ${raw.toLowerCase()}`, colorClass: 'text-gray-400', isNumeric: false };
      }
      case 'exchange': {
        const ex = getExchange(r);
        return { value: NaN, text: ex.toLowerCase(), colorClass: getExchangeColor(ex), isNumeric: false };
      }
      case 'source':
        return { value: NaN, text: String(r._source ?? '').toLowerCase(), colorClass: 'text-gray-500', isNumeric: false };
      case 'sebelum':
        return {
          value: isPercent
            ? parseNum(r['Persentase (Hari Sebelumnya)'], r._source, true)
            : sharesToLot(r['Jumlah Saham (Hari Sebelumnya)'], r._source),
          text: '',
          colorClass: 'text-gray-400',
          isNumeric: true,
        };
      case 'sesudah':
        return {
          value: isPercent
            ? parseNum(r['Persentase (Hari Ini)'], r._source, true)
            : sharesToLot(r['Jumlah Saham (Hari Ini)'], r._source),
          text: '',
          colorClass: getChangeColor(r),
          isNumeric: true,
        };
      case 'perubahan':
        return {
          value: isPercent
            ? (() => {
                const prevPct = parseNum(r['Persentase (Hari Sebelumnya)'], r._source, true);
                const currPct = parseNum(r['Persentase (Hari Ini)'], r._source, true);
                return (!isNaN(prevPct) && !isNaN(currPct)) ? currPct - prevPct : NaN;
              })()
            : sharesToLot(r['Perubahan'], r._source),
          text: '',
          colorClass: getChangeColor(r),
          isNumeric: true,
        };
      default:
        return { value: NaN, text: '', colorClass: null, isNumeric: false };
    }
  };

  const processedData = useMemo(() => {
    let result = [...deduplicatedData];

    const f = columnFilters;
    const fields = ['date', 'kode', 'pemegang', 'rekening', 'status', 'sebelum', 'sesudah', 'perubahan', 'exchange', 'source'];
    for (const field of fields) {
      const expr = f[field];
      if (!expr || !expr.trim()) continue;
      result = result.filter(r => evaluateFilterExpression(expr, getCellForField(r, field)));
    }

    result.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortConfig.key) {
        case 'date':
          aVal = a._date ?? '';
          bVal = b._date ?? '';
          break;
        case 'kode':
          aVal = a['Kode Efek'] ?? '';
          bVal = b['Kode Efek'] ?? '';
          break;
        case 'pemegang':
          aVal = a['Nama Pemegang Saham'] ?? '';
          bVal = b['Nama Pemegang Saham'] ?? '';
          break;
        case 'rekening':
          aVal = a['Nama Rekening Efek'] ?? '';
          bVal = b['Nama Rekening Efek'] ?? '';
          break;
        case 'sebelum':
          aVal = appliedDataType === 'percent'
            ? parseNum(a['Persentase (Hari Sebelumnya)'], a._source, true)
            : sharesToLot(a['Jumlah Saham (Hari Sebelumnya)'], a._source);
          bVal = appliedDataType === 'percent'
            ? parseNum(b['Persentase (Hari Sebelumnya)'], b._source, true)
            : sharesToLot(b['Jumlah Saham (Hari Sebelumnya)'], b._source);
          aVal = isNaN(aVal) ? 0 : aVal;
          bVal = isNaN(bVal) ? 0 : bVal;
          break;
        case 'sesudah':
          aVal = appliedDataType === 'percent'
            ? parseNum(a['Persentase (Hari Ini)'], a._source, true)
            : sharesToLot(a['Jumlah Saham (Hari Ini)'], a._source);
          bVal = appliedDataType === 'percent'
            ? parseNum(b['Persentase (Hari Ini)'], b._source, true)
            : sharesToLot(b['Jumlah Saham (Hari Ini)'], b._source);
          aVal = isNaN(aVal) ? 0 : aVal;
          bVal = isNaN(bVal) ? 0 : bVal;
          break;
        case 'perubahan':
          if (appliedDataType === 'percent') {
            const aPrevPct = parseNum(a['Persentase (Hari Sebelumnya)'], a._source, true);
            const aCurrPct = parseNum(a['Persentase (Hari Ini)'], a._source, true);
            aVal = (!isNaN(aPrevPct) && !isNaN(aCurrPct)) ? aCurrPct - aPrevPct : 0;

            const bPrevPct = parseNum(b['Persentase (Hari Sebelumnya)'], b._source, true);
            const bCurrPct = parseNum(b['Persentase (Hari Ini)'], b._source, true);
            bVal = (!isNaN(bPrevPct) && !isNaN(bCurrPct)) ? bCurrPct - bPrevPct : 0;
          } else {
            aVal = sharesToLot(a['Perubahan'], a._source);
            bVal = sharesToLot(b['Perubahan'], b._source);
          }
          aVal = isNaN(aVal) ? 0 : aVal;
          bVal = isNaN(bVal) ? 0 : bVal;
          break;
        case 'status':
          aVal = a['Status (Lokal/Asing)'] ?? '';
          bVal = b['Status (Lokal/Asing)'] ?? '';
          break;
        case 'exchange':
          aVal = getExchange(a);
          bVal = getExchange(b);
          break;
        case 'source':
          aVal = a._source ?? '';
          bVal = b._source ?? '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [deduplicatedData, columnFilters, sortConfig, appliedDataType, exchangeMap]);

  const thClass = (col: SortKey) =>
    `px-1 py-0 text-center whitespace-nowrap cursor-pointer select-none hover:bg-white/10 transition-colors`;

  return (
    <div className={`flex flex-col ${isDashboard ? 'h-full' : 'h-[calc(100vh-64px)]'} relative`}>
      {loading && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }} />
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* ── Top Control Bar ── */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-6 md:gap-y-2">

          {/* Date Range — inline */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap text-white">Date Range:</label>

            {/* From */}
            <div className="relative flex items-center">
              <input
                ref={dateFromRef}
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
                style={{ colorScheme: 'dark' }}
              />
            </div>

            <span className="text-sm text-gray-400">to</span>

            {/* To */}
            <div className="relative flex items-center">
              <input
                ref={dateToRef}
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>

          {/* Data Type */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap text-white">Data:</label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value as 'volume' | 'percent')}
              className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors cursor-pointer"
            >
              <option value="volume">Volume</option>
              <option value="percent">%</option>
            </select>
          </div>

          {/* Data Source */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap text-white">Source:</label>
            <select
              value={dataSource}
              onChange={(e) => {
                setDataSource(e.target.value as 'idx' | 'emiten' | 'all');
                // Trigger refresh if needed
              }}
              className="h-8 px-2 bg-background border border-input rounded text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors cursor-pointer"
            >
              <option value="idx">IDX</option>
              <option value="emiten">Emiten</option>
              <option value="all">All</option>
            </select>
          </div>

          {/* Show Button */}
          <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto">
            <button
              onClick={handleFetch}
              disabled={loading || !dateFrom}
              className="h-8 px-4 bg-white text-black hover:bg-gray-200 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Show
            </button>
            <button
              onClick={() => {
                setColumnFilters({});
                setSortConfig({ key: 'date', direction: 'desc' });
              }}
              className="h-8 px-3 bg-transparent border border-white/20 text-white hover:bg-white/10 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5 rotate-180" />
              Reset
            </button>
          </div>

        </div>
      </div>

      {/* ── Table ── */}
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full">
        <div className="flex-auto overflow-y-auto overflow-x-auto" style={{ willChange: 'transform' }}>
          <table className="w-full text-[12px] tabular-nums min-w-[700px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>

              {/* Header Row */}
              <tr className="h-[22px] divide-x divide-white/30 border-b border-white/30">
                <th onClick={() => handleSort('date')} className={`${thClass('date')} w-[80px]`}>
                  Tanggal<SortIndicator col="date" />
                </th>
                <th onClick={() => handleSort('kode')} className={`${thClass('kode')} w-[70px]`}>
                  Ticker<SortIndicator col="kode" />
                </th>
                <th onClick={() => handleSort('pemegang')} className={`${thClass('pemegang')} min-w-[180px]`}>
                  Pemegang Saham<SortIndicator col="pemegang" />
                </th>
                <th onClick={() => handleSort('rekening')} className={`${thClass('rekening')} min-w-[100px]`}>
                  Rekening Efek<SortIndicator col="rekening" />
                </th>
                <th onClick={() => handleSort('status')} className={`${thClass('status')} w-[50px]`}>
                  F/D<SortIndicator col="status" />
                </th>
                <th onClick={() => handleSort('sebelum')} className={`${thClass('sebelum')} w-[110px]`}>
                  Sebelum<SortIndicator col="sebelum" />
                </th>
                <th onClick={() => handleSort('sesudah')} className={`${thClass('sesudah')} w-[110px]`}>
                  Sesudah<SortIndicator col="sesudah" />
                </th>
                <th onClick={() => handleSort('perubahan')} className={`${thClass('perubahan')} w-[110px]`}>
                  Perubahan<SortIndicator col="perubahan" />
                </th>
                <th onClick={() => handleSort('exchange')} className={`${thClass('exchange')} w-[80px]`}>
                  Exchange<SortIndicator col="exchange" />
                </th>
                <th onClick={() => handleSort('source')} className={`${thClass('source')} w-[80px]`}>
                  Source<SortIndicator col="source" />
                </th>
              </tr>

              {/* Filter Row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.date || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, date: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.kode || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, kode: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-left text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.pemegang || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, pemegang: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.rekening || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, rekening: val }))}
                  />
                </td>
                {/* F/D filter */}
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="F/D"
                    value={columnFilters.status || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, status: val }))}
                  />
                </td>
                {/* Sebelum, Sesudah, Perubahan — numeric filter inputs */}
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder=">0"
                    value={columnFilters.sebelum || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, sebelum: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder=">0"
                    value={columnFilters.sesudah || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, sesudah: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder=">0"
                    value={columnFilters.perubahan || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, perubahan: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.exchange || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, exchange: val }))}
                  />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput
                    type="text"
                    className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                    placeholder="Filter"
                    value={columnFilters.source || ''}
                    onChange={(val) => setColumnFilters(prev => ({ ...prev, source: val }))}
                  />
                </td>
              </tr>
            </thead>

            <tbody>
              {processedData.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    Tidak ada data untuk rentang tanggal ini
                  </td>
                </tr>
              ) : (
                processedData.map((row, idx) => {
                  const rekeningRaw = row['Nama Rekening Efek'] ?? '';
                  const brokerCode = resolveBrokerCode(rekeningRaw, brokers);
                  const rekeningDisplay = brokerCode ?? rekeningRaw;

                  const isPercent = appliedDataType === 'percent';
                  const changeColor = getChangeColor(row);
                  const borderClass = '';

                  let changeDisplay: React.ReactNode;
                  if (isPercent) {
                    const prevPct = parseNum(row['Persentase (Hari Sebelumnya)'], row._source, true);
                    const currPct = parseNum(row['Persentase (Hari Ini)'], row._source, true);
                    const pct = (!isNaN(prevPct) && !isNaN(currPct)) ? currPct - prevPct : NaN;
                    if (isNaN(pct)) {
                      changeDisplay = '-';
                    } else {
                      changeDisplay = `${pct > 0 ? '+' : ''}${pct.toLocaleString('en-US', row._source === 'emiten'
                        ? { minimumFractionDigits: 3, maximumFractionDigits: 3 }
                        : { maximumFractionDigits: 2 })}%`;
                    }
                  } else {
                    const lotChange = sharesToLot(row['Perubahan'], row._source);
                    changeDisplay = isNaN(lotChange) ? '-' : (
                      <>{lotChange > 0 ? '+' : ''}{lotChange.toLocaleString('en-US', { maximumFractionDigits: 0 })}</>
                    );
                  }

                  const exchange = getExchange(row);
                  const exchangeColor = getExchangeColor(exchange);

                  return (
                    <tr
                      key={idx}
                      className="hover:bg-white/[0.25] transition-colors h-[22px] divide-x divide-white/30"
                    >
                      <td className={`px-2 py-0 text-center text-gray-400 ${borderClass}`}>
                        {formatDisplayDate(row._date)}
                      </td>
                      <td
                        className={`px-2 py-0 text-center font-medium text-white cursor-help ${borderClass}`}
                        title={row['Nama Emiten']}
                      >
                        {row['Kode Efek']}
                      </td>
                      <td
                        className={`px-2 py-0 text-left text-gray-300 truncate max-w-[240px] ${borderClass}`}
                        title={row['Nama Pemegang Saham']}
                      >
                        {row['Nama Pemegang Saham']}
                      </td>
                      <td
                        className={`px-2 py-0 text-center truncate max-w-[100px] ${getBrokerColor(brokerCode)} ${borderClass}`}
                        title={rekeningRaw}
                      >
                        {rekeningDisplay}
                      </td>
                      <td className={`px-2 py-0 text-center text-gray-400 ${borderClass}`}>
                        {row['Status (Lokal/Asing)'] === 'L'
                          ? 'D'
                          : row['Status (Lokal/Asing)'] === 'A'
                          ? 'F'
                          : row['Status (Lokal/Asing)']}
                      </td>
                      <td className={`px-2 py-0 text-right text-gray-400 ${borderClass}`}>
                        {isPercent
                          ? renderPercent(row['Persentase (Hari Sebelumnya)'], row._source)
                          : formatLot(row['Jumlah Saham (Hari Sebelumnya)'], row._source)}
                      </td>
                      <td className={`px-2 py-0 text-right font-medium ${changeColor} ${borderClass}`}>
                        {isPercent
                          ? renderPercent(row['Persentase (Hari Ini)'], row._source)
                          : formatLot(row['Jumlah Saham (Hari Ini)'], row._source)}
                      </td>
                      <td className={`px-2 py-0 text-right font-medium ${changeColor} ${borderClass}`}>
                        {changeDisplay}
                      </td>
                      <td className={`px-2 py-0 text-center font-semibold ${exchangeColor} ${borderClass}`}>
                        {exchange}
                      </td>
                      <td className={`px-2 py-0 text-center text-gray-500 uppercase text-[10px] ${borderClass}`}>
                        {row._source}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
